import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localeJa from '@angular/common/locales/ja';
import localeKo from '@angular/common/locales/ko';
import localeNl from '@angular/common/locales/nl';
import localeRu from '@angular/common/locales/ru';
import localeTr from '@angular/common/locales/tr';
import { LOCALE_ID } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Keychain } from '@ionic-native/keychain/ngx';
import { IonicStorageModule } from '@ionic/storage';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, from } from 'rxjs';

import {
  ajsCatalogsServiceProvider,
  ajsCustomRouterProvider,
  ajsMessagesServiceProvider,
  ajsMigrationHelperServiceServiceProvider,
  ajsTasksServiceProvider,
  ajsTopToolbarProvider,
  ajsTriageServiceProvider,
} from 'src/admin-front/ajs-upgraded-providers';
import { ClientVersionInterceptor } from 'src/app/core/interceptors/client-version.interceptor';
import { JWTHeaderInterceptor } from 'src/app/core/interceptors/jwt-header.interceptor';
import { MaintenanceModeInterceptor } from 'src/app/core/interceptors/maintenance-mode.interceptor';
import { alcmeonSupportedLanguages } from 'src/app/mobile/login/supported-languages.const';
import { AlcmeonSupportedLanguages } from 'src/app/mobile/login/supported-languages.type';
import { AlcmeonContextService } from './core/alcmeon-context/alcmeon-context.service';
import { AlcmeonSessionInterceptor } from './core/interceptors/alcmeon-session.interceptor';
import { TrackInterceptor } from './core/interceptors/track.interceptor';
import { TrackLoggerService } from './core/track/track-logger.service';
import { DateTimezonePipe } from './shared/pipes/date-timezone.pipe';

/**
 * This files groups code used to init both app.module (for the native app build) and admin-front.module (for the desktop build)
 */

// The Date pipe needs locale data for the language code returned by the DynamicLocaleId class below
function registerLocalesForSupportedLanguages() {
  registerLocaleData(localeDe, 'de');
  // seems like locale for 'en' is registered by default
  registerLocaleData(localeEs, 'es');
  registerLocaleData(localeFr, 'fr');
  registerLocaleData(localeIt, 'it');
  registerLocaleData(localeNl, 'nl');
  registerLocaleData(localeRu, 'ru');
  registerLocaleData(localeTr, 'tr');
  registerLocaleData(localeKo, 'ko');
  registerLocaleData(localeJa, 'ja');
  // languages codes not supported by the platform.
  registerLocaleData(localeEn, 'pl');
}

// thanks https://github.com/angular/angular/issues/15039#issuecomment-456345066 for this nice hack
// and thanks https://github.com/angular/angular-cli/issues/10673#issuecomment-473901065 for the tip to export it
class DynamicLocaleId extends String {
  constructor(private service: TranslateService) {
    super('');
  }
  toString() {
    return this.service.currentLang;
  }
}

/**
 * Translations are fetched via webpack dynamic import,
 * so that the hashed bundle name serves for cache busting.
 * See https://github.com/ngx-translate/http-loader
 * and https://webpack.js.org/api/module-methods/#dynamic-expressions-in-import
 */
class WebpackTranslateLoader implements TranslateLoader {
  getTranslation(lang: AlcmeonSupportedLanguages): Observable<object> {
    const hasSupportedLanguage = alcmeonSupportedLanguages.includes(lang);
    if (!hasSupportedLanguage) {
      console.info(`Language "${lang}" not supported, fallback to english.`);
    }
    const languageCode = hasSupportedLanguage ? lang : 'en';
    return from(import(`../assets/i18n/${languageCode}.json`));
  }
}

export const initCtxAndTranslationService = (
  alcmeonContextService: AlcmeonContextService,
  translateService: TranslateService,
  trackLoggerService: TrackLoggerService
) => {
  return async () => {
    // Add locale info for date pipes, etc
    registerLocalesForSupportedLanguages();
    // As ionic-storage has an async API, and as the alcmeon context needs to be loaded before page start making back-end calls,
    // it is loaded here (so that angular waits for the promise to resolve before starting the app)
    trackLoggerService.log(`before init storage`);
    await alcmeonContextService.initFromStoragePromise();
    trackLoggerService.log(`after init storage - hasJwt: ${alcmeonContextService.hasJwt()}`);
    translateService.addLangs(['en', 'fr', 'es']);
    translateService.setDefaultLang('en');
    const browserLang = translateService.getBrowserLang();
    const langToUse = alcmeonContextService.lang || browserLang;
    translateService.use(translateService.getLangs().includes(langToUse) ? langToUse : 'en');
    await translateService.get('m/ok').toPromise(); // do not boot the app until the tranlations are ready (we use .instant() a lot)
  };
};

export const commonImports = [
  BrowserModule,
  BrowserAnimationsModule,
  HttpClientModule,
  IonicStorageModule.forRoot({
    driverOrder: ['indexeddb', 'localstorage'],
  }),
  TranslateModule.forRoot({
    loader: {
      provide: TranslateLoader,
      useClass: WebpackTranslateLoader,
    },
  }),
];

export const commonProviders = [
  Keychain,
  AlcmeonContextService,
  { provide: LOCALE_ID, useClass: DynamicLocaleId, deps: [TranslateService] },
  { provide: HTTP_INTERCEPTORS, useClass: ClientVersionInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AlcmeonSessionInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: JWTHeaderInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: MaintenanceModeInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: TrackInterceptor, multi: true },
  {
    provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
    useValue: {
      showDelay: 300,
      hideDelay: 0,
      position: 'above',
      touchendHideDelay: 0,
      disableTooltipInteractivity: true, // can't be passed as property in template
    },
  },
  ajsCustomRouterProvider,
  ajsTopToolbarProvider,
  ajsTasksServiceProvider,
  ajsMigrationHelperServiceServiceProvider,
  ajsTriageServiceProvider,
  ajsCatalogsServiceProvider,
  ajsMessagesServiceProvider,
  DatePipe,
  DateTimezonePipe,
];
